import { errorSchema } from "@app/shared";
import type { ZodType, z } from "zod";
import { pasteConcertsResultSchema } from "../../../shared/src/app/concerts";
import { accountErasureSchema } from "../../../shared/src/app/export";
import { nextQuestionSchema } from "../../../shared/src/app/question-draw";
import {
  createdShareLinkSchema,
  sharedFriseSchema,
  shareLinkSchema,
  shareLinksSchema,
} from "../../../shared/src/app/share";
import {
  type createEntryBodySchema,
  entrySchema,
  friseSchema,
  type InvitedRole,
  managedTrackSchema,
  managedTracksSchema,
  memberSchema,
  membersSchema,
  parsedDateSchema,
  type proposalBodySchema,
  proposalSchema,
  proposalsSchema,
  settingsSchema,
} from "../../../shared/src/app/timeline";
import { ApiError, apiGet } from "../lib/api";

export type {
  PasteConcertsResult,
  PastedBand,
} from "../../../shared/src/app/concerts";
export {
  type DrawnQuestion,
  type NextQuestion,
  nextQuestionSchema,
  type StageGap,
} from "../../../shared/src/app/question-draw";
export {
  LIFE_STAGE_CHIPS,
  LIFE_STAGE_NAMES,
  LIFE_STAGE_WHEN,
  type LifeStage,
} from "../../../shared/src/app/questions";
/**
 * Pont vers le contrat de Ma timeline, cote frontal, et les appels types du backend (copie du
 * modele de `apps/cinepass/packages/frontend/src/app/api.ts`).
 *
 * Le `exports` de `packages/shared` n'expose que son `src/index.ts`, qui appartient au template :
 * le chemin relatif traverse la frontiere de paquet sans toucher a un fichier du template. Il est
 * ecrit ici et nulle part ailleurs cote frontal : le reste de l'app importe le contrat depuis
 * `./api`.
 *
 * Les lectures passent par `apiGet` du template, les ecritures par `apiSend`, plus bas : les deux
 * rendent les memes `ApiError`. Une ecriture porte l'en-tete `Origin`, pose tout seul par le
 * navigateur pour un `fetch` de meme origine ; la garde du serveur l'exige.
 */
export * from "../../../shared/src/app/share";
export * from "../../../shared/src/app/timeline";

export type SendMethod = "POST" | "PUT" | "PATCH" | "DELETE";

/** Envoie une ecriture en JSON. `schema` `null` : la reponse est ignoree (un `204`). */
export async function apiSend<T>(
  method: SendMethod,
  path: string,
  body: unknown,
  schema: ZodType<T>,
): Promise<T>;
export async function apiSend(
  method: SendMethod,
  path: string,
  body: unknown,
  schema: null,
): Promise<undefined>;
export async function apiSend<T>(
  method: SendMethod,
  path: string,
  body: unknown,
  schema: ZodType<T> | null,
): Promise<T | undefined> {
  const headers: Record<string, string> = { accept: "application/json" };
  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    headers["content-type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const response = await fetch(path, init);
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const parsed = errorSchema.safeParse(payload);
    throw new ApiError(
      parsed.success ? parsed.data.error.code : "http_error",
      parsed.success ? parsed.data.error.message : `Erreur HTTP ${response.status}`,
      response.status,
    );
  }
  if (schema === null) return undefined;
  const payload: unknown = await response.json().catch(() => null);
  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
  }
  return result.data;
}

/** Les clefs TanStack. Tout ce qui est prive vit sous `["t", ...]`. */
export const queryKeys = {
  frise: ["t", "frise"] as const,
  settings: ["t", "reglages"] as const,
  parse: (raw: string) => ["t", "parse", raw] as const,
  questions: ["t", "questions"] as const,
  concerts: (text: string) => ["t", "concerts", text] as const,
  members: ["t", "proches"] as const,
  proposals: ["t", "propositions"] as const,
  managedTracks: ["t", "pistes"] as const,
  links: ["t", "liens"] as const,
  /** Hors de `["t", ...]` : la page d'un lien n'a pas de session, ni rien de prive en cache. */
  sharedFrise: (token: string) => ["v", token] as const,
};

/** Vrai quand le serveur dit « pas de session » : l'ecran de connexion prend la place. */
export const isSignedOut = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

/** Vrai quand la session existe mais que l'adresse n'est pas admise. */
export const isRefused = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 403 && error.code !== "owner_only";

/** Vrai quand un proche ouvre une page du proprietaire seul (questions, reglages, proches). */
export const isOwnerOnly = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 403 && error.code === "owner_only";

export const fetchFrise = () => apiGet("/api/t/frise", friseSchema);
export const fetchSettings = () => apiGet("/api/t/reglages", settingsSchema);

export const parseDate = (raw: string) =>
  apiSend("POST", "/api/t/dates/parse", { raw }, parsedDateSchema);

/** Le corps d'une creation, tel que le client l'ecrit (les defauts sont poses par le serveur). */
export type CreateEntryInput = z.input<typeof createEntryBodySchema>;

export const createEntry = (body: CreateEntryInput) =>
  apiSend("POST", "/api/t/points", body, entrySchema);

export const updateEntry = (id: number, body: CreateEntryInput) =>
  apiSend("PATCH", `/api/t/points/${String(id)}`, body, entrySchema);

export const deleteEntry = (id: number) =>
  apiSend("DELETE", `/api/t/points/${String(id)}`, undefined, null);

export const fetchNextQuestion = () => apiGet("/api/t/questions/suivante", nextQuestionSchema);

const questionPath = (id: string, action: string) =>
  `/api/t/questions/${encodeURIComponent(id)}/${action}`;

/** Repond a une question : le point nait (ou, pour un concert, le point a dater recoit sa date). */
export const answerQuestion = (id: string, body: CreateEntryInput) =>
  apiSend("POST", questionPath(id, "reponse"), body, entrySchema);

export const skipQuestion = (id: string) =>
  apiSend("POST", questionPath(id, "passer"), undefined, null);

export const postponeQuestion = (id: string) =>
  apiSend("POST", questionPath(id, "plus-tard"), undefined, null);

/** L'apercu du collage des concerts : rien n'est ecrit. */
export const previewConcerts = (text: string) =>
  apiSend("POST", "/api/t/concerts/coller", { text }, pasteConcertsResultSchema);

/** L'ajout des groupes choisis (`selected`, leurs noms), chacun en point « a dater ». */
export const pasteConcerts = (text: string, selected: string[]) =>
  apiSend("POST", "/api/t/concerts/coller", { text, selected }, pasteConcertsResultSchema);

export const updateSettings = (birthDate: string | null) =>
  apiSend("PATCH", "/api/t/reglages", { birthDate }, settingsSchema);

export const fetchMembers = () => apiGet("/api/t/proches", membersSchema);

/** Invite une adresse : rien n'est envoye, la personne demande son lien elle-meme. */
export const inviteMember = (email: string, role: InvitedRole) =>
  apiSend("POST", "/api/t/proches", { email, role }, memberSchema);

/** Retire un proche : son acces tombe tout de suite. */
export const revokeMember = (id: number) =>
  apiSend("DELETE", `/api/t/proches/${String(id)}`, undefined, null);

/** Les propositions en attente : toutes pour le proprietaire, les siennes pour un proche. */
export const fetchProposals = () => apiGet("/api/t/propositions", proposalsSchema);

export type ProposalInput = z.input<typeof proposalBodySchema>;
/** Le corps de l'acceptation, tel que le client l'ecrit. */
export type AcceptProposalInput = { entry?: CreateEntryInput };

export const createProposal = (body: ProposalInput) =>
  apiSend("POST", "/api/t/propositions", body, proposalSchema);

/** Accepte une proposition, telle quelle (`{}`) ou modifiee (`{ entry }`). */
export const acceptProposal = (id: number, body: AcceptProposalInput = {}) =>
  apiSend("POST", `/api/t/propositions/${String(id)}/accepter`, body, entrySchema);

export const refuseProposal = (id: number) =>
  apiSend("POST", `/api/t/propositions/${String(id)}/refuser`, undefined, null);

/** Demande le lien magique ; le clic dans le courrier ramene sur `/`. */
export const requestMagicLink = (email: string) =>
  apiSend("POST", "/api/auth/sign-in/magic-link", { email, callbackURL: "/" }, null);

export const signOut = () => apiSend("POST", "/api/auth/sign-out", undefined, null);

/** Les deux exports du proprietaire : des liens, le serveur repond en piece jointe datee. */
export const EXPORT_JSON_PATH = "/api/t/export.json";
export const EXPORT_CSV_PATH = "/api/t/export.csv";

/**
 * Efface ce que la frise garde sur la personne connectee (toute la frise pour le proprietaire, son
 * acces et ses propositions pour un proche), puis son compte par la route du template.
 */
export async function deleteMyAccount(): Promise<void> {
  await apiSend("DELETE", "/api/t/compte", undefined, accountErasureSchema);
  await apiSend("DELETE", "/api/me", undefined, null);
}

// La gestion des pistes (Reglages > Pistes, #588) : proprietaire seul.
export const fetchManagedTracks = () => apiGet("/api/t/pistes", managedTracksSchema);

export const createTrack = (name: string, color: string) =>
  apiSend("POST", "/api/t/pistes", { name, color }, managedTrackSchema);

export const updateTrack = (
  id: number,
  patch: { name?: string; color?: string; hidden?: boolean },
) => apiSend("PATCH", `/api/t/pistes/${String(id)}`, patch, managedTrackSchema);

/** Range les pistes : tous les identifiants, dans le nouvel ordre. */
export const reorderTracks = (ids: number[]) =>
  apiSend("PUT", "/api/t/pistes/ordre", { ids }, managedTracksSchema);

/** Supprime une piste vide ; 409 `track_not_empty` quand elle porte un point ou une proposition. */
export const deleteTrack = (id: number) =>
  apiSend("DELETE", `/api/t/pistes/${String(id)}`, undefined, null);

// Les liens de partage (#588) : la gestion est au proprietaire, la lecture est publique.
export const fetchLinks = () => apiGet("/api/t/liens", shareLinksSchema);

/** Cree un lien. Le jeton n'est rendu qu'ici, une seule fois : l'ecran doit le montrer a ce moment. */
export const createLink = (label: string, trackIds: number[]) =>
  apiSend("POST", "/api/t/liens", { label, trackIds }, createdShareLinkSchema);

export const updateLink = (id: number, patch: { label?: string; trackIds?: number[] }) =>
  apiSend("PATCH", `/api/t/liens/${String(id)}`, patch, shareLinkSchema);

export const revokeLink = (id: number) =>
  apiSend("DELETE", `/api/t/liens/${String(id)}`, undefined, null);

/** La frise d'un lien : sans session. `404 link_unavailable` pour un lien inconnu ou revoque. */
export const fetchSharedFrise = (token: string) =>
  apiGet(`/api/v/${encodeURIComponent(token)}`, sharedFriseSchema);

/** L'adresse complete d'un lien, a envoyer : l'origine de l'app et `/v/<jeton>`. */
export const shareUrl = (token: string): string => `${window.location.origin}/v/${token}`;
